/**
 * Removing a record entered in error, and putting it back (ADHERE-plus "voiding").
 *
 * Nothing clinical is ever destroyed. A removed patient (`active: false` with the removal
 * extension) or a removed episode (`entered-in-error` with the extension) stays on the server and
 * on devices, readable by id, but leaves every list, search, register, report, follow-up and
 * supervision count — `liveResources` is the one filter they all go through. The extension says
 * who removed it, when and why; an admin can restore it, and the record returns exactly as it was.
 */
import { EXT_REMOVED, patientIdOf, } from './fhir.js';
const extsOf = (r) => (r.extension ?? []);
/** Who removed this record, when and why; null when it is not removed. */
export function removalOf(r) {
    const ext = extsOf(r).find((x) => x.url === EXT_REMOVED);
    if (!ext)
        return null;
    const sub = (ext.extension ?? []);
    const get = (k) => sub.find((x) => x.url === k);
    return {
        reason: get('reason')?.valueString ?? '',
        by: get('by')?.valueString ?? '',
        at: get('at')?.valueDateTime ?? '',
    };
}
const removalExt = (x, extra = []) => ({
    url: EXT_REMOVED,
    extension: [
        { url: 'reason', valueString: x.reason.slice(0, 500) },
        { url: 'by', valueString: x.by.slice(0, 120) },
        { url: 'at', valueDateTime: x.at },
        ...extra,
    ],
});
export const isRemovedPatient = (p) => p.active === false && removalOf(p) !== null;
export const isRemovedEpisode = (e) => e.status === 'entered-in-error';
export function removePatient(p, x) {
    return {
        ...p,
        active: false,
        extension: [...extsOf(p).filter((e) => e.url !== EXT_REMOVED), removalExt(x)],
    };
}
/** The record as it was before it was removed (`active` is dropped, not set to true). */
export function restorePatient(p) {
    const { active: _a, extension: _e, ...rest } = p;
    const extension = extsOf(p).filter((e) => e.url !== EXT_REMOVED);
    return { ...rest, ...(extension.length ? { extension } : {}) };
}
/** Remove an episode opened in error; its status and end are kept so a restore puts them back. */
export function removeEpisode(e, x) {
    const keep = [{ url: 'previous-status', valueCode: e.status }];
    if (e.period?.end)
        keep.push({ url: 'previous-end', valueString: e.period.end });
    return {
        ...e,
        status: 'entered-in-error',
        period: {
            ...(e.period ?? { start: x.at.slice(0, 10) }),
            end: e.period?.end ?? x.at.slice(0, 10),
        },
        extension: [...extsOf(e).filter((y) => y.url !== EXT_REMOVED), removalExt(x, keep)],
    };
}
export function restoreEpisode(e) {
    const ext = extsOf(e).find((y) => y.url === EXT_REMOVED);
    const sub = (ext?.extension ?? []);
    const status = (sub.find((y) => y.url === 'previous-status')?.valueCode ??
        'active');
    const end = sub.find((y) => y.url === 'previous-end')?.valueString;
    const period = { ...(e.period ?? { start: '' }) };
    if (end)
        period.end = end;
    else
        Reflect.deleteProperty(period, 'end');
    const extension = extsOf(e).filter((y) => y.url !== EXT_REMOVED);
    return {
        ...e,
        status: status === 'entered-in-error' ? 'active' : status,
        period,
        extension,
    };
}
const refId = (r) => {
    const ref = r?.reference ?? '';
    const i = ref.indexOf('/');
    return i > 0 ? ref.slice(i + 1) : null;
};
/**
 * The records that count: without removed patients (and everything about them), removed episodes,
 * the visits recorded in a removed episode, and what those visits recorded.
 */
export function liveResources(resources) {
    const removedPatients = new Set(resources
        .filter((r) => r.resourceType === 'Patient' && isRemovedPatient(r))
        .map((r) => r.id));
    const removedEpisodes = new Set(resources
        .filter((r) => r.resourceType === 'EpisodeOfCare' && isRemovedEpisode(r))
        .map((r) => r.id));
    const removedEncounters = new Set();
    for (const r of resources) {
        if (r.resourceType !== 'Encounter')
            continue;
        const eps = (r.episodeOfCare ?? []).map(refId);
        if (eps.length && eps.every((id) => id && removedEpisodes.has(id)))
            removedEncounters.add(r.id);
    }
    return resources.filter((r) => {
        if (removedPatients.has(patientIdOf(r)))
            return false;
        if (r.resourceType === 'EpisodeOfCare')
            return !removedEpisodes.has(r.id);
        if (r.resourceType === 'Encounter')
            return !removedEncounters.has(r.id);
        const enc = refId(r.encounter);
        return !(enc && removedEncounters.has(enc));
    });
}
//# sourceMappingURL=removal.js.map